import "../assets/scss/Map.scss";

import * as chroma from "chroma-js";
import { observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { Glyphicon, Overlay, Popover } from "react-bootstrap";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";

import { IImageService } from "../services/image/IImageService";
import { DictionaryStore } from "../stores/dictionary/DictionaryStore";
import { ILoanWordsStore, WordFilter } from "../stores/word/LoanWordsStore";
import { WordTooltip } from "./WordTooltip";

import ReactDOM = require("react-dom");

// TODO should really calc dynamically
const HIGH_COLOR_COUNT = 20;

export interface MapProps {
  dictionaryStore: DictionaryStore;
  filter: WordFilter;
  wordStore: ILoanWordsStore;
  onClick: (country: string | null) => void;
  imageService: IImageService;
}

@observer
export class Map extends React.Component<MapProps, undefined> {
  render() {
    return <RegionMap {...this.props} />;
  }
}

const wrapperStyles = {
  position: "relative" as "relative",
  width: "100%",
  margin: "0 auto"
};

const COLOR_COUNT = 32;

const colorScale = chroma
  .scale(["#00B8D4", "FFD740", "#FF6E40"])
  .mode("lch")
  .colors(COLOR_COUNT);

const mapData = require("./../assets/json/world-50m-with-population.json");

@observer
class RegionMap extends React.Component<MapProps, { mapVersion: number }> {
  private selectedGeography: Geography | null = null;

  @observable
  private isNavigating = false;
  @observable
  private isTooltipClosed = false;

  componentWillReceiveProps(newProps: MapProps) {
    if (this.hasCountryChanged(newProps)) {
      this.setAsNavigating();
    }
  }

  private hasCountryChanged(newProps: MapProps): boolean {
    return (
      newProps.filter.originalLanguage !== this.props.filter.originalLanguage
    );
  }

  render() {
    const { filter, wordStore } = this.props;

    const words = wordStore.getWords(filter);

    // has duplicates, but does not matter:
    const countriesMatchingFilter = words.map(word => word.country);

    const allCountries = wordStore.allWords.map(word => word.country);

    const tooltipTitle = this.getTooltipTitle();

    return (
      <div style={wrapperStyles} onClick={this.onClickEmptyMapPart}>
        <ComposableMap
          projectionConfig={{
            scale: 205,
            rotation: [-11, 0, 0]
          }}
          width={980}
          height={551}
          style={{
            width: "100%",
            height: "auto"
          }}
        >
          <ZoomableGroup center={[0, 20]} disablePanning={true}>
            <Geographies geography={mapData} disableOptimization>
              {(geographies, projection) =>
                this.processGeographies(
                  geographies,
                  projection,
                  countriesMatchingFilter,
                  allCountries
                )
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {!!tooltipTitle && (
          <Overlay
            show={true}
            onHide={() => {}}
            placement={this.getTooltipPosition()}
            container={this}
            target={() => this.getTooltipTarget()}
          >
            <Popover
              id="popover-basic"
              className="map_popover"
              title={tooltipTitle}
              style={{ minWidth: "500px" }}
            >
              <Glyphicon
                glyph="remove"
                className="map_closeIcon"
                onClick={this.onClickCloseTooltip}
              />
              <WordTooltip
                dictionaryStore={this.props.dictionaryStore}
                filter={filter}
                wordStore={wordStore}
                imageService={this.props.imageService}
              />
            </Popover>
          </Overlay>
        )}
      </div>
    );
  }

  private onClickEmptyMapPart = () => {
    this.props.onClick(null);
  };

  private getTooltipPosition = (): string => {
    const language = this.props.filter.originalLanguage;

    const atBottom = ["Russian"];
    if (atBottom.indexOf(language) >= 0) {
      return "bottom";
    }

    const atRight = [
      "Inuit",
      "Hawaiian",
      "Amerindian",
      "French (USA)",
      "Spanish (USA)",
      "Zulu"
    ];
    if (atRight.indexOf(language) >= 0) {
      return "right";
    }

    const atLeft = [
      "Australian aboriginal",
      "Chinese",
      "Hindi",
      "Japanese",
      "Malagasy",
      "Malaysian",
      "Maori",
      "Persian",
      "Sanskrit",
      "Tahitian",
      "Tamil",
      "Tongan",
      "Urdu",
      "Zulu"
    ];
    if (atLeft.indexOf(language) >= 0) {
      return "left";
    }

    return "right";
  };

  private divOfMexico: any | null = null;
  private divOfItaly: any | null = null;

  // hack - since making the map take 100% height,
  // the north USA tooltips do not position correctly.
  // So try to use Mexico as the anchor.
  // Similar issue for France, Spain.
  private getTooltipTarget = () => {
    const language = this.props.filter.originalLanguage;

    const isNorthAmerican =
      [
        "Amerindian",
        "French (USA)",
        "Hawaiian",
        "Inuit",
        "Spanish (USA)"
      ].indexOf(language) >= 0;

    if (!this.divOfMexico) {
      throw new Error("Cannot find Mexico");
    }

    const isNearItaly = ["French", "Spanish"].indexOf(language) >= 0;

    if (!this.divOfItaly) {
      throw new Error("Cannot find Italy");
    }

    let region = this.selectedGeography;
    if (isNorthAmerican) {
      region = this.divOfMexico;
    } else if (isNearItaly) {
      region = this.divOfItaly;
    }

    const node = ReactDOM.findDOMNode(region);
    return node;
  };

  private getTooltipTitle = (): string | undefined => {
    const { wordStore, filter } = this.props;
    const wordCount = wordStore.getWordsNotFilteredByWord(filter).length;

    const tooltipTitle =
      !this.isTooltipClosed &&
      wordCount > 0 &&
      !this.isNavigating &&
      filter.originalLanguage &&
      `${filter.originalLanguage} words in English (${wordCount})`;

    return tooltipTitle;
  };

  private onClickCloseTooltip = () => {
    this.isTooltipClosed = true;
  };

  private processGeographies = (
    geographies,
    projection,
    countriesMatchingFilter,
    allCountries
  ) => {
    return geographies.map((geography, i) => {
      const sheetCountry = this.mapRegionNameToSheetCountryName(
        geography.properties.name
      );

      const languagesThisCountry = this.props.wordStore.getLanguagesFromCountry(
        sheetCountry
      );

      const isThisLanguageSelected =
        languagesThisCountry.indexOf(this.props.filter.originalLanguage) >= 0;

      return (
        <Geography
          key={i}
          geography={geography}
          projection={projection}
          onClick={this.onClickRegion}
          style={this.getCountryStyle(
            sheetCountry,
            countriesMatchingFilter,
            allCountries
          )}
        >
          {isThisLanguageSelected && (
            <desc>
              <div
                className="selectedGeography"
                style={{ width: 10, height: 10 }}
                ref={this.bindGeography}
              />
            </desc>
          )}
          {sheetCountry === "Italy" && (
            <desc>
              <div
                className="italyGeography"
                style={{ width: 10, height: 10 }}
                ref={this.bindItalyGeography}
              />
            </desc>
          )}
          {sheetCountry === "Mexico" && (
            <desc>
              <div
                className="mexicoGeography"
                style={{ width: 10, height: 10 }}
                ref={this.bindMexicoGeography}
              />
            </desc>
          )}
        </Geography>
      );
    });
  };

  private bindGeography = (geography: HTMLDivElement | null) => {
    this.selectedGeography = geography
      ? geography.parentElement.parentElement
      : null;
  };

  private bindMexicoGeography = (geography: HTMLDivElement | null) => {
    this.divOfMexico = geography ? geography.parentElement.parentElement : null;
  };

  private bindItalyGeography = (geography: HTMLDivElement | null) => {
    this.divOfItaly = geography ? geography.parentElement.parentElement : null;
  };

  private getCountryStyle(
    sheetCountry: string,
    countriesMatchingFilter,
    allCountries
  ) {
    const isMatch = allCountries.indexOf(sheetCountry) >= 0;

    const defaultStyle = {
      fill: chroma(
        this.getColorScale(
          sheetCountry,
          countriesMatchingFilter,
          allCountries,
          true
        )
      ),
      stroke: "#607D8B",
      strokeWidth: 0.75,
      outline: "none"
    };

    return {
      default: defaultStyle,
      hover: isMatch
        ? {
            cursor: "pointer",
            fill: chroma(
              this.getColorScale(sheetCountry, allCountries, allCountries)
            ).darken(0.5),
            stroke: "#607D8B",
            strokeWidth: 0.75,
            outline: "none"
          }
        : defaultStyle,
      pressed: isMatch
        ? {
            fill: chroma(
              this.getColorScale(sheetCountry, allCountries, allCountries)
            ).brighten(0.5),
            stroke: "#607D8B",
            strokeWidth: 0.75,
            outline: "none"
          }
        : defaultStyle
    };
  }

  private getColorScale(
    sheetCountry: string,
    countriesMatchingFilter: string[],
    allCountries: string[],
    isDefault: boolean = false
  ) {
    // clean up country names to match the map:
    const index = countriesMatchingFilter.indexOf(sheetCountry);

    if (index >= 0) {
      // calculate a color based on % of total words

      const { wordStore } = this.props;

      const languages = wordStore.getLanguagesFromCountry(sheetCountry);

      const wordCount = languages
        .map(language => wordStore.getWordCountForLanguage(language))
        .reduce((prev, current) => prev + current);

      const highestWordCount = Math.max(HIGH_COLOR_COUNT, wordCount);
      const colorIndex = Math.round(
        (wordCount / highestWordCount) * (COLOR_COUNT - 1)
      );

      return colorScale[colorIndex];
    }

    const hasCountryWords = allCountries.some(
      country => country === sheetCountry
    );
    if (isDefault && hasCountryWords) {
      // country has words, but does not match current filter
      return "#eeeeee";
    }

    return "white";
  }

  private mapRegionNameToSheetCountryName(country: string): string {
    if (country === "Netherlands") {
      return "The Netherlands";
    }
    if (country === "United States") {
      return "USA";
    }
    if (country === "Dem. Rep. Congo") {
      return "Democratic Republic of the Congo";
    }
    if (country === "Polynesia") {
      return "Fiji";
    }
    return country;
  }

  private onClickRegion = (region: Geography, event: React.MouseEvent<any>) => {
    this.setAsNavigating();

    this.isNavigating = true;
    this.isTooltipClosed = false;

    // filter by country - need to convert back into google sheets names
    const country = this.mapRegionNameToSheetCountryName(
      region.properties.name
    );

    this.props.onClick(country);

    // prevent handling by onClickEmptyMapPart()
    event.stopPropagation();
    event.preventDefault();
  };

  // close and re-open the tooltip, to avoid showing stale image for previous word:
  private setAsNavigating() {
    this.isNavigating = true;
    this.isTooltipClosed = false;
    setTimeout(() => (this.isNavigating = false), 500);
  }
}



// WEBPACK FOOTER //
// ./components/Map.tsx